import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LineChart } from '@shopify/polaris-viz';
import { useI18n } from '@shopify/react-i18n';
import { useAuthFetch } from '../../../hooks/useAuthFetch';
import { useCurrency } from '../../../contexts/CurrencyContext';
import enTranslations from '../translations/en.json';
import frTranslations from '../translations/fr.json';

const translationsByLocale = { en: enTranslations, fr: frTranslations };

// Date range presets
const DATE_RANGES = {
  today: { days: 0, label: 'today' },
  yesterday: { days: 1, label: 'yesterday' },
  last7: { days: 7, label: 'last7' },
  last28: { days: 28, label: 'last28' },
  last90: { days: 90, label: 'last90' },
  last12months: { type: 'last12months', label: 'last12months' },
  thisYear: { type: 'thisYear', label: 'thisYear' },
  lastYear: { type: 'lastYear', label: 'lastYear' },
};

function getDateRange(preset) {
  const now = new Date();
  const config = DATE_RANGES[preset];

  if (config.type === 'last12months') {
    const from = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    return { from, to: now };
  }
  if (config.type === 'thisYear') {
    return { from: new Date(now.getFullYear(), 0, 1), to: now };
  }
  if (config.type === 'lastYear') {
    return {
      from: new Date(now.getFullYear() - 1, 0, 1),
      to: new Date(now.getFullYear() - 1, 11, 31),
    };
  }
  if (config.days === 0) {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return { from: today, to: now };
  }
  if (config.days === 1) {
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    return { from: yesterday, to: yesterday };
  }
  const from = new Date(now.getTime() - config.days * 24 * 60 * 60 * 1000);
  return { from, to: now };
}

function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

export default function SalesChart({ defaultRange = 'last28', defaultGranularity = 'day', compact = false, title = null }) {
  const authFetch = useAuthFetch();
  const currency = useCurrency();
  const [i18n] = useI18n({
    id: 'SalesChart',
    fallback: enTranslations,
    translations(locale) {
      return translationsByLocale[locale] ?? enTranslations;
    },
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState(defaultRange);
  const [granularity, setGranularity] = useState(defaultGranularity);

  const dateRangeOptions = useMemo(() => [
    { value: 'today',        label: i18n.translate('Tracking.chart.ranges.today') },
    { value: 'yesterday',    label: i18n.translate('Tracking.chart.ranges.yesterday') },
    { value: 'last7',        label: i18n.translate('Tracking.chart.ranges.last7') },
    { value: 'last28',       label: i18n.translate('Tracking.chart.ranges.last28') },
    { value: 'last90',       label: i18n.translate('Tracking.chart.ranges.last90') },
    { value: 'last12months', label: i18n.translate('Tracking.chart.ranges.last12months') },
    { value: 'thisYear',     label: i18n.translate('Tracking.chart.ranges.thisYear') },
    { value: 'lastYear',     label: i18n.translate('Tracking.chart.ranges.lastYear') },
  ], [i18n]);

  const granularityOptions = useMemo(() => [
    { value: 'day',   label: i18n.translate('Tracking.chart.granularity.day') },
    { value: 'month', label: i18n.translate('Tracking.chart.granularity.month') },
  ], [i18n]);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    const { from, to } = getDateRange(selectedRange);
    const params = new URLSearchParams({
      dateFrom: formatDate(from),
      dateTo: formatDate(to),
      granularity,
      status: 'confirmed',
    });

    try {
      const res = await authFetch(`/api/sales/analytics?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setData(json.data || []);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch, selectedRange, granularity]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;
    return [
      {
        name: i18n.translate('Tracking.chart.series.conversions'),
        data: data.map((d) => ({ key: d.date, value: d.conversions ?? 0 })),
      },
      {
        name: i18n.translate('Tracking.chart.series.revenue'),
        data: data.map((d) => ({ key: d.date, value: d.revenue ?? 0 })),
      },
    ];
  }, [data, i18n, currency]);

  const selectedRangeLabel = dateRangeOptions.find((o) => o.value === selectedRange)?.label || '';
  const selectedGranularityLabel = granularityOptions.find((o) => o.value === granularity)?.label || '';

  return (
    <s-section>
      <s-stack direction="block" gap="base">
        {/* Title — shown only when provided */}
        {title && (
          <strong style={{ fontSize: '13px', fontWeight: '600' }}>{title}</strong>
        )}
        {/* Controls — hidden in compact mode */}
        {!compact && (
          <s-stack direction="inline" justifyContent="space-between" alignItems="center">
            {/* Date range picker via s-menu */}
            <s-button commandFor="date-range-menu">{selectedRangeLabel}</s-button>
            <s-menu id="date-range-menu" accessibilityLabel={i18n.translate('Tracking.chart.dateRangeLabel')}>
              {dateRangeOptions.map((opt) => (
                <s-button
                  key={opt.value}
                  onClick={() => setSelectedRange(opt.value)}
                >
                  {opt.label}
                </s-button>
              ))}
            </s-menu>

            {/* Granularity picker via s-menu */}
            <s-button commandFor="granularity-menu">{selectedGranularityLabel}</s-button>
            <s-menu id="granularity-menu" accessibilityLabel={i18n.translate('Tracking.chart.granularityLabel')}>
              {granularityOptions.map((opt) => (
                <s-button
                  key={opt.value}
                  onClick={() => {
                    setGranularity(opt.value);
                    if (opt.value === 'month') setSelectedRange('last12months');
                  }}
                >
                  {opt.label}
                </s-button>
              ))}
            </s-menu>
          </s-stack>
        )}

        {/* Chart */}
        <s-box>
          {loading ? (
            <s-stack direction="inline" justifyContent="center" alignItems="center" gap="base">
              <s-spinner />
            </s-stack>
          ) : !chartData ? (
            <s-stack direction="inline" justifyContent="center" alignItems="center">
              <s-text tone="subdued">{i18n.translate('Tracking.chart.empty')}</s-text>
            </s-stack>
          ) : (
            <LineChart
              data={chartData}
              theme="Light"
              showLegend
              xAxisOptions={{
                labelFormatter: (value) => {
                  if (granularity === 'month') {
                    const [year, month] = value.split('-');
                    return new Date(year, month - 1).toLocaleDateString(i18n.locale, {
                      month: 'short',
                      year: 'numeric',
                    });
                  }
                  const date = new Date(value);
                  return date.toLocaleDateString(i18n.locale, { month: 'short', day: 'numeric' });
                },
              }}
              yAxisOptions={{
                labelFormatter: (value) => `${Number(value).toFixed(2)} ${currency}`,
              }}
              tooltipOptions={{
                valueFormatter: (value, index) =>
                  index === 1
                    ? `${Number(value).toFixed(2)} ${currency}`
                    : String(Math.round(value)),
              }}
            />
          )}
        </s-box>
      </s-stack>
    </s-section>
  );
}
